import { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminHttp, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, scoreColor, formatApiError } from "../../lib/adminApi";

export default function Pipeline() {
  const [leadsByStatus, setLeadsByStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        STATUS_ORDER.map((s) =>
          adminHttp.get(`/admin/leads?status=${s}&limit=100&sort=score_desc`).then((r) => [s, r.data.items || []])
        )
      );
      const map = {};
      results.forEach(([s, items]) => { map[s] = items; });
      setLeadsByStatus(map);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const totalsByStatus = useMemo(() => {
    const t = {};
    STATUS_ORDER.forEach((s) => {
      const items = leadsByStatus[s] || [];
      t[s] = {
        count: items.length,
        value: items.reduce((acc, l) => acc + (l.estimated_value_mxn || 0), 0),
      };
    });
    return t;
  }, [leadsByStatus]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const srcCol = source.droppableId;
    const dstCol = destination.droppableId;
    const prev = leadsByStatus;

    const moved = prev[srcCol][source.index];
    const srcList = Array.from(prev[srcCol]);
    srcList.splice(source.index, 1);
    const dstList = Array.from(prev[dstCol] || []);
    dstList.splice(destination.index, 0, { ...moved, status: dstCol });

    setLeadsByStatus({ ...prev, [srcCol]: srcList, [dstCol]: dstList });

    if (srcCol !== dstCol) {
      try {
        await adminHttp.patch(`/admin/leads/${draggableId}`, { status: dstCol });
        toast.success(`Movido a ${STATUS_LABELS[dstCol]}`);
      } catch (err) {
        toast.error(formatApiError(err, "No se pudo mover el lead"));
        setLeadsByStatus(prev);
      }
    }
  };

  if (loading) return <div className="p-8 text-[#6B7280] font-mono text-xs uppercase tracking-widest">Cargando pipeline…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="admin-pipeline">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="divider-accent" /><span className="kicker">Kanban</span>
          </div>
          <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">
            Pipeline
          </h1>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUS_ORDER.map((status) => {
            const totals = totalsByStatus[status] || { count: 0, value: 0 };
            return (
              <Droppable droppableId={status} key={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-shrink-0 w-72 bg-white border-t-2 border transition-colors ${snapshot.isDraggingOver ? "border-accent bg-[#F2F4ED]" : "border-[#DDE2D5]"}`}
                    style={{ borderTopColor: STATUS_COLORS[status] }}
                    data-testid={`pipeline-column-${status}`}
                  >
                    <div className="px-4 py-3 border-b border-[#DDE2D5]">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-black uppercase text-sm tracking-tight" style={{ color: STATUS_COLORS[status] }}>
                          {STATUS_LABELS[status]}
                        </span>
                        <span className="font-mono text-xs text-[#4B5563]">{totals.count}</span>
                      </div>
                      {totals.value > 0 && (
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">
                          ${totals.value.toLocaleString("es-MX")} MXN
                        </p>
                      )}
                    </div>

                    <div className="p-3 space-y-2 min-h-[200px]">
                      {(leadsByStatus[status] || []).map((lead, idx) => (
                        <Draggable draggableId={lead.id} index={idx} key={lead.id}>
                          {(p, snap) => (
                            <Link
                              to={`/admin/leads/${lead.id}`}
                              ref={p.innerRef}
                              {...p.draggableProps}
                              {...p.dragHandleProps}
                              className={`block bg-[#F2F4ED] border border-[#DDE2D5] p-3 cursor-grab transition-all ${snap.isDragging ? "border-accent shadow-2xl" : "hover:border-accent"}`}
                              data-testid={`pipeline-card-${lead.id}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <p className="font-display font-bold text-sm text-[#1F2937] leading-tight truncate flex-1">
                                  {lead.full_name || lead.email || "—"}
                                </p>
                                <span
                                  className="font-display font-black text-sm tabular-nums flex-shrink-0"
                                  style={{ color: scoreColor(lead.score || 0) }}
                                >
                                  {lead.score || 0}
                                </span>
                              </div>
                              <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#6B7280] truncate">
                                {lead.city || "—"} · {lead.square_meters || "—"}
                              </p>
                              {lead.estimated_value_mxn > 0 && (
                                <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-accent">
                                  ${lead.estimated_value_mxn.toLocaleString("es-MX")}
                                </p>
                              )}
                            </Link>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
